import React, { useState, useEffect } from 'react';
import { ref, onValue, set, get, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { toastService } from '../utils/toast';
import { 
  Coins, 
  Plus, 
  Minus, 
  Trash2, 
  Search, 
  Filter,
  Package,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Users,
  Calendar
} from 'lucide-react';

interface CoinsData {
  [userId: string]: {
    balance: number;
    transactions: Array<{
      id: string;
      type: 'purchase' | 'spend' | 'bonus' | 'refund';
      amount: number;
      timestamp: number;
      description: string;
      packageId?: string;
      paymentMethod?: string;
    }>;
    lastUpdated: number;
  };
}

interface CoinPackage {
  id: string;
  name: string;
  coins: number;
  price: number;
  currency: string;
  popular?: boolean;
  discount?: number;
  description: string;
}

const CoinsManagement: React.FC = () => {
  const [coinsData, setCoinsData] = useState<CoinsData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [addType, setAddType] = useState<'purchase' | 'bonus' | 'refund'>('purchase');
  const [addDescription, setAddDescription] = useState('');
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<CoinPackage | null>(null);
  const auth = getAuth();

  // Aranytallér csomagok
  const coinPackages: CoinPackage[] = [
    {
      id: 'starter',
      name: 'Kezdő Csomag',
      coins: 100,
      price: 2.99,
      currency: 'USD',
      description: 'Tökéletes kezdőknek'
    },
    {
      id: 'popular',
      name: 'Népszerű Csomag',
      coins: 500,
      price: 9.99,
      currency: 'USD',
      popular: true,
      description: 'Legjobb ár-érték arány'
    },
    {
      id: 'pro',
      name: 'Pro Csomag',
      coins: 1000,
      price: 17.99,
      currency: 'USD',
      discount: 10,
      description: 'Kedvezményes nagy csomag'
    },
    {
      id: 'premium',
      name: 'Prémium Csomag',
      coins: 2500,
      price: 39.99,
      currency: 'USD',
      discount: 20,
      description: 'Legnagyobb kedvezmény'
    }
  ];

  useEffect(() => {
    if (!auth.currentUser) return;

    const coinsRef = ref(realtimeDb, 'coins');

    const unsubscribe = onValue(coinsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCoinsData(data);
      } else {
        setCoinsData({});
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching coins data:', error);
      
      // Mock adatok fejlesztéshez
      const mockCoinsData: CoinsData = {
        'user1@example.com': {
          balance: 1500,
          transactions: [
            {
              id: 'tx1',
              type: 'purchase',
              amount: 1000,
              timestamp: Date.now() - 86400000,
              description: 'Aranytallér vásárlás - Pro Csomag',
              packageId: 'pro',
              paymentMethod: 'App Store'
            },
            {
              id: 'tx2',
              type: 'spend',
              amount: -200,
              timestamp: Date.now() - 3600000,
              description: 'Játék díj'
            }
          ],
          lastUpdated: Date.now() - 3600000
        },
        'user2@example.com': {
          balance: 2500,
          transactions: [
            {
              id: 'tx3',
              type: 'purchase',
              amount: 2000,
              timestamp: Date.now() - 172800000,
              description: 'Aranytallér vásárlás - Prémium Csomag',
              packageId: 'premium',
              paymentMethod: 'Google Play'
            },
            {
              id: 'tx4',
              type: 'bonus',
              amount: 500,
              timestamp: Date.now() - 7200000,
              description: 'Regisztrációs bónusz'
            }
          ],
          lastUpdated: Date.now() - 7200000
        }
      };
      
      setCoinsData(mockCoinsData);
      setError('Firebase jogosultsági hiba - mock adatok használata');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth.currentUser]);

  const addCoins = async (userId: string, amount: number, type: string, description: string, packageId?: string, paymentMethod?: string) => {
    const loadingToast = toastService.loading('Aranytallérok hozzáadása...');
    try {
      const userCoinsRef = ref(realtimeDb, `coins/${userId}`);
      const currentData = coinsData[userId] || { balance: 0, transactions: [] };
      
      const newBalance = currentData.balance + amount;
      const newTransaction = {
        id: `tx_${Date.now()}`,
        type,
        amount,
        timestamp: Date.now(),
        description,
        packageId,
        paymentMethod
      };

      const updatedData = {
        ...currentData,
        balance: newBalance,
        transactions: [...(currentData.transactions || []), newTransaction],
        lastUpdated: Date.now()
      };

      await set(userCoinsRef, updatedData);
      console.log(`Added ${amount} coins to user ${userId}`);
      toastService.dismiss(loadingToast);
      toastService.admin.coinTransaction(amount, userId);
    } catch (error) {
      console.error('Error adding coins:', error);
      setError('Hiba történt az aranytallérok hozzáadása során');
      toastService.dismiss(loadingToast);
      toastService.error('Hiba történt az aranytallérok hozzáadása során');
    }
  };

  const removeCoins = async (userId: string, amount: number, description: string) => {
    try {
      const userCoinsRef = ref(realtimeDb, `coins/${userId}`);
      const currentData = coinsData[userId];
      
      if (!currentData || currentData.balance < amount) {
        setError('Nincs elegendő aranytallér');
        return;
      }

      const newBalance = currentData.balance - amount;
      const newTransaction = {
        id: `tx_${Date.now()}`,
        type: 'spend',
        amount: -amount,
        timestamp: Date.now(),
        description
      };

      const updatedData = {
        ...currentData,
        balance: newBalance,
        transactions: [...(currentData.transactions || []), newTransaction],
        lastUpdated: Date.now()
      };

      await set(userCoinsRef, updatedData);
      console.log(`Removed ${amount} coins from user ${userId}`);
    } catch (error) {
      console.error('Error removing coins:', error);
      setError('Hiba történt az aranytallérok levonása során');
    }
  };

  const deleteUserCoins = async (userId: string) => {
    if (window.confirm(`Biztosan törölni szeretnéd ${userId} aranytallér adatait?`)) {
      try {
        await remove(ref(realtimeDb, `coins/${userId}`));
        console.log(`Deleted coins data for user ${userId}`);
      } catch (error) {
        console.error('Error deleting user coins:', error);
        setError('Hiba történt a felhasználó aranytallér adatainak törlése során');
      }
    }
  };

  const getTotalCoins = () => {
    return Object.values(coinsData).reduce((total, userData) => total + userData.balance, 0);
  };

  const getTotalTransactions = () => {
    return Object.values(coinsData).reduce((total, userData) => 
      total + (userData.transactions?.length || 0), 0);
  };

  const getTotalPurchases = () => {
    return Object.values(coinsData).reduce((total, userData) => {
      const purchases = userData.transactions?.filter(tx => tx.type === 'purchase') || [];
      return total + purchases.reduce((sum, tx) => sum + tx.amount, 0);
    }, 0);
  };

  const getTotalRevenue = () => {
    return Object.values(coinsData).reduce((total, userData) => {
      const purchases = userData.transactions?.filter(tx => tx.type === 'purchase') || [];
      return total + purchases.reduce((sum, tx) => {
        // Becsült bevétel (átlagos 0.01 USD/coin)
        return sum + (tx.amount * 0.01);
      }, 0);
    }, 0);
  };

  const filteredUsers = Object.entries(coinsData).filter(([userId, userData]) => {
    const searchLower = searchTerm.toLowerCase();
    return userId.toLowerCase().includes(searchLower) ||
           userData.transactions?.some(tx => 
             tx.description.toLowerCase().includes(searchLower)
           );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Aranytallér Kezelés</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Aranytallér tranzakciók és vásárlások kezelése
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
          <button 
            onClick={() => setShowPackagesModal(true)}
            className="btn-secondary w-full sm:w-auto px-4 py-3 sm:py-2"
          >
            <Package className="mr-2 h-4 w-4" />
            Csomagok
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary w-full sm:w-auto px-4 py-3 sm:py-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Aranytallér Hozzáadása
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Coins className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes Aranytallér</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getTotalCoins().toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShoppingCart className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Vásárlások</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getTotalPurchases().toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Bevétel</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">${getTotalRevenue().toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Felhasználók</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{Object.keys(coinsData).length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés felhasználó vagy tranzakció alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary">
                <Filter className="mr-2 h-4 w-4" />
                Szűrő
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Felhasználók Aranytallér Adatai</h3>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Felhasználó
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Egyenleg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tranzakciók
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Utolsó Frissítés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map(([userId, userData]) => (
                  <tr key={userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{userId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        <Coins className="inline h-4 w-4 text-yellow-500 mr-1" />
                        {userData.balance.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {userData.transactions?.length || 0} tranzakció
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(userData.lastUpdated).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(userId);
                            setShowAddModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteUserCoins(userId)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Coins Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Aranytallér Hozzáadása
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="form-label">Felhasználó</label>
                  <select
                    value={selectedUser || ''}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Válassz felhasználót</option>
                    {Object.keys(coinsData).map(userId => (
                      <option key={userId} value={userId}>{userId}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Összeg</label>
                  <input
                    type="number"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    className="form-input"
                    placeholder="Adja meg az összeget"
                  />
                </div>
                
                <div>
                  <label className="form-label">Típus</label>
                  <select
                    value={addType}
                    onChange={(e) => setAddType(e.target.value as any)}
                    className="form-input"
                  >
                    <option value="purchase">Vásárlás</option>
                    <option value="bonus">Bónusz</option>
                    <option value="refund">Visszatérítés</option>
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Leírás</label>
                  <input
                    type="text"
                    value={addDescription}
                    onChange={(e) => setAddDescription(e.target.value)}
                    className="form-input"
                    placeholder="Tranzakció leírása"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Mégse
                </button>
                <button
                  onClick={async () => {
                    if (selectedUser && addAmount) {
                      await addCoins(selectedUser, parseInt(addAmount), addType, addDescription);
                      setShowAddModal(false);
                      setSelectedUser(null);
                      setAddAmount('');
                      setAddDescription('');
                    }
                  }}
                  className="btn-primary"
                >
                  Hozzáadás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coin Packages Modal */}
      {showPackagesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Aranytallér Csomagok
                </h3>
                <button
                  onClick={() => setShowPackagesModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {coinPackages.map((pkg) => (
                  <div key={pkg.id} className={`card ${pkg.popular ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="card-body text-center">
                      {pkg.popular && (
                        <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full mb-2">
                          NÉPSZERŰ
                        </div>
                      )}
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {pkg.name}
                      </h4>
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        <Coins className="inline h-6 w-6 text-yellow-500 mr-1" />
                        {pkg.coins.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {pkg.description}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        ${pkg.price}
                      </div>
                      {pkg.discount && (
                        <div className="text-green-600 text-sm mb-4">
                          {pkg.discount}% kedvezmény
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowPackagesModal(false);
                          setShowAddModal(true);
                          setAddAmount(pkg.coins.toString());
                          setAddType('purchase');
                          setAddDescription(`${pkg.name} vásárlás`);
                        }}
                        className="btn-primary w-full"
                      >
                        Választás
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Hiba
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinsManagement; 